
import React, { useState, useEffect } from 'react';
import { Language, Lead, BlogPost } from './types';
import { TRANSLATIONS } from './constants';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { AdminPanel } from './components/AdminPanel';
import { LanguageSwitcher } from './components/LanguageSwitcher';
import { StorageService } from './services/db';

// Pages
import { Home } from './pages/Home';
import { Investment } from './pages/Investment';
import { FAQ } from './pages/FAQ';
import { Gallery } from './pages/Gallery';
import { Blog } from './pages/Blog';
import { PostDetail } from './pages/PostDetail';

const App: React.FC = () => {
  const [lang, setLang] = useState<Language>('CS');
  const t = TRANSLATIONS[lang];
  const [currentPage, setCurrentPage] = useState<string>('home');
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [syncKey, setSyncKey] = useState(0);

  const [leads, setLeads] = useState<Lead[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = window.innerHeight;
      const scrolledPastHero = window.scrollY >= heroHeight - 10;
      setIsHeaderSticky(scrolledPastHero);
    };
    
    handleScroll();
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentPage]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setIsMobileMenuOpen(false);
    if (currentPage !== 'home') {
      setIsHeaderSticky(true);
    }
  }, [currentPage, selectedPostId]);

  // Lock scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const handleLeadSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const newLead: Lead = {
      id: Math.random().toString(36).substr(2, 9),
      name: (formData.get('name') as string) || 'Anonymous',
      email: (formData.get('email') as string) || 'no-email',
      phone: (formData.get('phone') as string) || 'no-phone',
      interest: (formData.get('interest') as any) || 'glamping',
      timestamp: Date.now(),
      status: 'new',
    };
    setLeads([newLead, ...leads]);
    alert('Thank you! Our manager will contact you soon.');
    e.currentTarget.reset();
  };

  const handleExportCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Interest', 'Status', 'Date'];
    const rows = leads.map(l => [l.name, l.email, l.phone, l.interest, l.status, new Date(l.timestamp).toLocaleDateString()]);
    const csvContent = "data:text/csv;charset=utf-8," + headers.join(",") + "\n" + rows.map(e => e.join(",")).join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", "stealth_leads.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const updateLeadStatus = (id: string, status: 'read') => {
    setLeads(leads.map(l => l.id === id ? { ...l, status } : l));
  };

  const navigateToPost = (id: string) => {
    setSelectedPostId(id);
    setCurrentPage('post-detail');
  };

  const forceSync = () => setSyncKey(prev => prev + 1);

  const renderPage = () => {
    if (currentPage === 'post-detail' && selectedPostId) {
      const post = StorageService.getBlogs().find(b => b.id === selectedPostId);
      if (post) return <PostDetail post={post} onNavigate={setCurrentPage} />;
    }

    switch(currentPage) {
      case 'home': return <Home key={syncKey} t={t} onNavigate={setCurrentPage} onPostClick={navigateToPost} onSubmitLead={handleLeadSubmit} />;
      case 'investment': return <Investment t={t} />;
      case 'faq': return <FAQ key={syncKey} />;
      case 'gallery': return <Gallery />;
      case 'blog': return <Blog key={syncKey} onPostClick={navigateToPost} />;
      case 'contacts': return (
        <div className="pt-48 pb-48 bg-white text-center px-6">
          <h1 className="text-4xl font-light uppercase mb-8">Contacts</h1>
          <div className="max-w-xl mx-auto space-y-4 font-bold">
            <p>Phone: +420 608 099 059</p>
            <p>Email: domivka@email.cz</p>
          </div>
        </div>
      );
      default: return <Home t={t} onNavigate={setCurrentPage} onPostClick={navigateToPost} onSubmitLead={handleLeadSubmit} />;
    }
  };

  if (isAdmin) {
    return (
      <div className="min-h-screen bg-stone-50">
        <div className="bg-stone-900 text-white p-4 flex justify-between items-center px-8">
          <span className="font-bold tracking-widest text-xs text-stone-400">STEALTH <span className="text-white">CMS V1.0</span></span>
          <button onClick={() => setIsAdmin(false)} className="text-[10px] font-bold uppercase tracking-widest opacity-60 hover:opacity-100 transition-opacity">Exit CMS</button>
        </div>
        <AdminPanel 
          leads={leads} 
          onUpdateStatus={updateLeadStatus} 
          onExport={handleExportCSV} 
          onSync={forceSync}
        />
      </div>
    );
  }

  const NavItemMobile = ({ id, label }: { id: string, label: string }) => (
    <button 
      onClick={() => {
        setCurrentPage(id);
        setIsMobileMenuOpen(false);
      }}
      className={`text-2xl font-black uppercase tracking-tighter transition-all py-6 text-left border-b border-stone-100 w-full ${
        currentPage === id ? 'text-stone-900' : 'text-stone-400'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="relative antialiased selection:bg-stone-900 selection:text-white">
      {/* Ticker Bar */}
      <div className="bg-stone-900 text-white py-2 overflow-hidden whitespace-nowrap text-[8px] font-black uppercase tracking-[0.3em] z-[70] border-b border-white/5 relative">
        <div className="animate-marquee inline-block">
          STEALTH MODULAR SYSTEMS • INSTALLATION 3-5 DAYS • ENERGY EFFICIENT A++ • PANORAMIC WINDOWS • ALL-SEASON LIVING • 365 DAYS PROFIT • STEALTH MODULAR SYSTEMS •
        </div>
      </div>

      <style>{`
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-marquee { animation: marquee 35s linear infinite; }
      `}</style>

      {/* Hero Section - Home Only */}
      {currentPage === 'home' && (
        <section className="relative h-[100vh] w-full flex flex-col justify-center items-center text-white text-center px-6 overflow-hidden">
          <div className="absolute inset-0 bg-black/40 z-10" />
          <video 
            autoPlay 
            muted 
            loop 
            playsInline 
            className="absolute inset-0 w-full h-full object-cover scale-105" 
            poster="https://lh3.googleusercontent.com/d/12BqNJDl2pNRil7oepqfTaLCSUITnTZHN"
          >
            <source src="https://assets.mixkit.co/videos/preview/mixkit-modern-architecture-building-with-glass-walls-32777-large.mp4" type="video/mp4" />
          </video>
          <div className="relative z-20 max-w-4xl space-y-10 animate-in fade-in slide-in-from-bottom-10 duration-1000">
            <div className="space-y-4">
              <p className="text-[10px] md:text-xs font-black uppercase tracking-[0.5em] opacity-80">Architectural Evolution</p>
              <h1 className="text-4xl md:text-8xl font-black uppercase tracking-tighter leading-none drop-shadow-2xl">{t.hero.title}</h1>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-5 pt-10">
              <button onClick={() => setCurrentPage('contacts')} className="w-full sm:w-auto px-12 py-5 bg-white text-stone-900 font-black uppercase tracking-widest rounded-full hover:bg-stone-100 transition-all transform hover:scale-105 active:scale-95 shadow-2xl text-[10px] md:text-xs">{t.hero.cta1}</button>
              <button onClick={() => setCurrentPage('investment')} className="w-full sm:w-auto px-12 py-5 bg-transparent border-2 border-white/40 backdrop-blur-md text-white font-black uppercase tracking-widest rounded-full hover:bg-white/10 transition-all text-[10px] md:text-xs">{t.hero.cta2}</button>
            </div>
          </div>
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 animate-bounce opacity-50">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7-7-7" /></svg>
          </div>
        </section>
      )}

      {/* Navigation */}
      <Header 
        lang={lang} 
        t={t} 
        onLangChange={setLang} 
        isSticky={isHeaderSticky} 
        isMenuOpen={isMobileMenuOpen}
        onMenuToggle={setIsMobileMenuOpen}
        onNavigate={setCurrentPage}
        currentPage={currentPage}
      />

      {/* Mobile Menu Overlay - Solid White Background, viewport fixed */}
      <div className={`fixed inset-0 bg-white z-[1000] transition-transform duration-500 ease-in-out transform flex flex-col ${
        isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex items-center justify-between px-6 py-6 border-b border-stone-100 bg-white">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-stone-900 rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-lg">S</span>
            </div>
            <span className="font-black tracking-tighter text-lg text-stone-900 uppercase">Stealth</span>
          </div>
          <button 
            onClick={() => setIsMobileMenuOpen(false)}
            className="p-3 text-stone-900 rounded-full bg-stone-50 active:scale-90 transition-transform"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6" />
            </svg>
          </button>
        </div>

        <div className="flex-grow overflow-y-auto bg-white px-8 py-6">
          <div className="space-y-1">
            <NavItemMobile id="home" label={t.header.howItWorks} />
            <NavItemMobile id="investment" label={t.header.investment} />
            <NavItemMobile id="gallery" label={t.header.gallery} />
            <NavItemMobile id="blog" label={t.header.blog} />
            <NavItemMobile id="faq" label="FAQ" />
            <NavItemMobile id="contacts" label={t.header.contacts} />
          </div>

          <div className="mt-12 space-y-8">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-stone-400 mb-6">Language Selection</p>
              <div className="grid grid-cols-3 gap-3">
                {['EN', 'DE', 'CS', 'SK', 'PL', 'UA'].map((l) => (
                  <button 
                    key={l}
                    onClick={() => { setLang(l as any); setIsMobileMenuOpen(false); }}
                    className={`px-4 py-4 rounded-2xl text-xs font-black uppercase border transition-all ${lang === l ? 'bg-stone-900 text-white border-stone-900' : 'text-stone-500 border-stone-100 bg-stone-50'}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6 bg-emerald-50 rounded-[2.5rem] border border-emerald-100 flex items-center justify-between">
              <div>
                 <p className="text-[10px] font-bold uppercase text-emerald-800 tracking-widest">WhatsApp Support</p>
                 <p className="text-sm font-black text-emerald-950">+420 608 099 059</p>
              </div>
              <a href="https://wa.me/420608099059" className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center text-white shadow-xl">
                 <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12.031 6.172c-3.181 0-5.767 2.586-5.767 5.767 0 1.267.405 2.436 1.096 3.389l-.715 2.614 2.678-.703c.82.505 1.776.797 2.802.797 3.181 0 5.767-2.586 5.767-5.767 0-3.181-2.586-5.767-5.861-5.767zm3.308 8.213c-.154.438-.764.795-1.127.842-.325.043-.75.076-1.213-.076-2.028-.667-3.342-2.731-3.441-2.864-.1-.133-.807-1.072-.807-2.046 0-.974.505-1.45.684-1.649.179-.199.392-.249.524-.249s.262.003.376.009c.125.007.292-.046.458.353.166.399.565 1.378.614 1.477.05.099.083.216.017.349s-.099.216-.199.332c-.099.116-.211.262-.303.353-.105.105-.216.216-.093.428.123.211.545.899 1.166 1.45.8.71 1.472.93 1.683 1.035.211.105.335.088.459-.055.123-.143.53-.615.671-.825.143-.21.285-.177.481-.105.196.072 1.242.585 1.455.694.213.109.355.163.407.253.052.09.052.522-.102.96z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Page Content */}
      <div className="relative z-0">
        {renderPage()}
      </div>

      <Footer onNavigate={setCurrentPage} onAdminEnter={() => setIsAdmin(true)} />

      <div className={`fixed bottom-8 left-8 z-50 transition-all duration-300 ${isHeaderSticky ? 'opacity-0 translate-y-10 pointer-events-none' : 'opacity-100 translate-y-0'}`}>
        <LanguageSwitcher current={lang} onSelect={setLang} variant="fixed" />
      </div>

      <div className="fixed bottom-8 right-8 z-50">
        <a href="https://wa.me/420608099059" target="_blank" className="w-14 h-14 bg-emerald-500 text-white rounded-full flex items-center justify-center shadow-2xl hover:bg-emerald-600 transition-all transform hover:scale-110 active:scale-95">
           <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24"><path d="M12.031 6.172c-3.181 0-5.767 2.586-5.767 5.767 0 1.267.405 2.436 1.096 3.389l-.715 2.614 2.678-.703c.82.505 1.776.797 2.802.797 3.181 0 5.767-2.586 5.767-5.767 0-3.181-2.586-5.767-5.861-5.767zm3.308 8.213c-.154.438-.764.795-1.127.842-.325.043-.75.076-1.213-.076-2.028-.667-3.342-2.731-3.441-2.864-.1-.133-.807-1.072-.807-2.046 0-.974.505-1.45.684-1.649.179-.199.392-.249.524-.249s.262.003.376.009c.125.007.292-.046.458.353.166.399.565 1.378.614 1.477.05.099.083.216.017.349s-.099.216-.199.332c-.099.116-.211.262-.303.353-.105.105-.216.216-.093.428.123.211.545.899 1.166 1.45.8.71 1.472.93 1.683 1.035.211.105.335.088.459-.055.123-.143.53-.615.671-.825.143-.21.285-.177.481-.105.196.072 1.242.585 1.455.694.213.109.355.163.407.253.052.09.052.522-.102.96z"/></svg>
        </a>
      </div>
    </div>
  );
};

export default App;
